// viewcamera.js
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  Tag,
  Badge,
  Descriptions,
  Avatar,
  Typography,
  Space,
  Button,
  Input,
  Select,
  Empty,
  Spin,
  Tooltip,
  Statistic,
  Row,
  Col,
  Divider,
  Alert,
  message,
  Breadcrumb,
  Form,
  Modal,
} from "antd";
import {
  CameraOutlined,
  SearchOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  WifiOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CalendarOutlined,
  UserOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import axios from "../../../utils/axios";
import Header from "../../header/header";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// ==========================================================================
// CONSTANTS
// ==========================================================================

const DEFAULT_PORT = "554";

const ipRules = [
  { required: true, message: "IP address is required" },
  {
    pattern: /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
    message: "Enter a valid IP address (e.g. 192.168.1.100)",
  },
];

const portRules = [
  { required: true, message: "Port is required" },
  {
    validator: (_, value) => {
      const num = Number(value);
      if (!value || isNaN(num) || num < 1 || num > 65535)
        return Promise.reject(new Error("Port must be 1–65535"));
      return Promise.resolve();
    },
  },
];

const channelRules = [
  { required: true, message: "Channel is required" },
  {
    pattern: /^[1-9]\d*$/,
    message: "Must be a positive integer",
  },
];

// ==========================================================================
// HELPERS
// ==========================================================================

const formatDate = (dateObj) => {
  if (!dateObj) return "—";
  const d = new Date(dateObj.$date || dateObj);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const AVATAR_COLORS = [
  "#4A90D9", "#52B788", "#E07B54", "#A678DE", "#F0A500", "#56B4D3",
];

const avatarColor = (name = "") =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

// ==========================================================================
// VALIDATION STATUS BADGE (reused from addcamera)
// ==========================================================================

function ValidationBadge({ status, error }) {
  if (status === "idle" || !status) return null;

  const map = {
    validating: {
      icon: <Loader2 className="w-4 h-4 animate-spin text-blue-500" style={{ display: "inline", marginRight: 6 }} />,
      label: "Validating…",
      style: { color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe" },
    },
    valid: {
      icon: <CheckCircle2 style={{ display: "inline", marginRight: 6, color: "#16a34a", width: 16, height: 16 }} />,
      label: "Stream validated",
      style: { color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0" },
    },
    invalid: {
      icon: <XCircle style={{ display: "inline", marginRight: 6, color: "#dc2626", width: 16, height: 16 }} />,
      label: error || "Validation failed",
      style: { color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca" },
    },
  };

  const { icon, label, style } = map[status] || {};
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 500,
        ...style,
      }}
    >
      {icon}
      {label}
    </span>
  );
}

// ==========================================================================
// EDIT CAMERA MODAL — only IP, Channel, Port editable
// ==========================================================================

function EditCameraModal({ cam, visible, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [saving, setSaving] = useState(false);

  const organizationId = localStorage.getItem("organizationId");
  const username = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").username || ""; }
    catch { return ""; }
  })();

  useEffect(() => {
    if (cam && visible) {
      form.setFieldsValue({
        ipAddress: cam.ipAddress,
        port: String(cam.port),
        channel: String(cam.channel),
      });
      setValidationStatus(null);
      setValidationError(null);
    }
  }, [cam, visible, form]);

  const buildRtspPreview = () => {
    const vals = form.getFieldsValue();
    if (!vals.ipAddress) return null;
    const auth = `${cam?.username || "admin"}:****`;
    return `rtsp://${auth}@${vals.ipAddress}:${vals.port || 554}/channel/${vals.channel || 1}`;
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const vals = form.getFieldsValue();
    setSaving(true);
    setValidationStatus("validating");
    setValidationError(null);

    try {
      // Build RTSP URL for validation
      const rtspUrl = `rtsp://${cam.username}:${cam.password || ""}@${vals.ipAddress}:${vals.port}/cam/channel/${vals.channel}`;

      const valRes = await axios.post("/validate-rtsp", {
        rtspUrl,
        cameraName: cam.cameraName,
        projectName: cam.projectName,
      });

      if (!valRes.data.success || !valRes.data.valid) {
        const errMsg = valRes.data.message || "Stream is not accessible";
        setValidationStatus("invalid");
        setValidationError(errMsg);
        message.error(errMsg);
        setSaving(false);
        return;
      }

      setValidationStatus("valid");

      // Persist the update
      const saveRes = await axios.post("/update-camera", {
        cameraCode: cam.code,
        organizationId,
        ipAddress: vals.ipAddress.trim(),
        port: Number(vals.port),
        channel: Number(vals.channel),
        modifiedBy: username,
      });

      if (saveRes.data.success) {
        message.success("Camera updated successfully");
        onSaved({
          ...cam,
          ipAddress: vals.ipAddress.trim(),
          port: Number(vals.port),
          channel: Number(vals.channel),
        });
        onClose();
      } else {
        message.error(saveRes.data.message || "Failed to save changes");
        setValidationStatus("invalid");
        setValidationError(saveRes.data.message || "Failed to save changes");
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || "Network error";
      setValidationStatus("invalid");
      setValidationError(errMsg);
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const rtspPreview = buildRtspPreview();

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "linear-gradient(135deg, #4A90D9, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditOutlined style={{ color: "#fff", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1A2742" }}>
              Edit Camera
            </div>
            <div style={{ fontSize: 12, color: "#7B8FB5", fontWeight: 400 }}>
              {cam?.cameraName}
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ValidationBadge status={validationStatus} error={validationError} />
          <Space>
            <Button onClick={onClose} icon={<CloseOutlined />}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
              style={{ background: "#4A90D9", border: "none" }}
            >
              Validate & Save
            </Button>
          </Space>
        </div>
      }
      width={480}
      styles={{ content: { borderRadius: 18 }, header: { borderRadius: "18px 18px 0 0" } }}
    >
      {/* Read-only info */}
      <div
        style={{
          background: "#F7F9FF",
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          border: "1px solid #E8EDF5",
        }}
      >
        <Text style={{ fontSize: 12, color: "#7B8FB5", display: "block", marginBottom: 4 }}>
          Read-only fields (contact admin to change)
        </Text>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          {[
            { label: "Name", value: cam?.cameraName },
            { label: "Location", value: cam?.location },
            { label: "Project", value: cam?.projectName },
            { label: "Username", value: cam?.username },
          ].map(({ label, value }) => (
            <div key={label}>
              <Text style={{ fontSize: 11, color: "#A0B0CC" }}>{label} </Text>
              <Text style={{ fontSize: 13, fontWeight: 600, color: "#4A5568" }}>{value || "—"}</Text>
            </div>
          ))}
        </div>
      </div>

      <Alert
        message="You can only edit IP Address, Port, and Channel."
        type="info"
        showIcon
        style={{ borderRadius: 10, marginBottom: 20, fontSize: 13 }}
      />

      <Form form={form} layout="vertical" size="middle">
        <Form.Item label="IP Address" name="ipAddress" rules={ipRules}>
          <Input
            placeholder="192.168.1.100"
            onChange={() => { setValidationStatus(null); setValidationError(null); }}
          />
        </Form.Item>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Form.Item label="Port" name="port" rules={portRules}>
            <Input
              type="number"
              placeholder="554"
              onChange={() => { setValidationStatus(null); setValidationError(null); }}
            />
          </Form.Item>
          <Form.Item label="Channel" name="channel" rules={channelRules}>
            <Input
              type="number"
              placeholder="1"
              onChange={() => { setValidationStatus(null); setValidationError(null); }}
            />
          </Form.Item>
        </div>

        {/* RTSP preview */}
        {rtspPreview && (
          <div
            style={{
              background: "#F8FAFF",
              border: "1px solid #E2E8F0",
              borderRadius: 10,
              padding: "10px 14px",
              marginTop: 4,
            }}
          >
            <Text style={{ fontSize: 11, color: "#A0B0CC", display: "block", marginBottom: 4 }}>
              RTSP Preview
            </Text>
            <code style={{ fontSize: 12, color: "#4A5568", wordBreak: "break-all" }}>
              {rtspPreview}
            </code>
          </div>
        )}
      </Form>
    </Modal>
  );
}

// ==========================================================================
// ADD CAMERA MODAL — full add form, inline in viewcamera
// ==========================================================================

function AddCameraModal({ visible, onClose, onAdded }) {
  const [form] = Form.useForm();
  const [connectionType, setConnectionType] = useState("direct");
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [saving, setSaving] = useState(false);

  const organizationId = localStorage.getItem("organizationId");
  const cafeId = localStorage.getItem("cafeId");
  const projectname = localStorage.getItem("projectname") || "";
  const username = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").username || ""; }
    catch { return ""; }
  })();

  // Fetch brand from project
  const [brand, setBrand] = useState("");
  useEffect(() => {
    if (!visible) return;
    const fetchBrand = async () => {
      try {
        const res = await axios.post("/fetch-createprojects", { username, organizationId });
        if (res.data.success) {
          const project = res.data.data.find((p) => p.projectname === projectname);
          if (project?.selectedCameraModel?.length > 0) {
            setBrand(project.selectedCameraModel[0].modelname);
          }
        }
      } catch { /* silent */ }
    };
    fetchBrand();
  }, [visible, username, organizationId, projectname]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ port: DEFAULT_PORT, projectName: projectname });
      setConnectionType("direct");
      setValidationStatus(null);
      setValidationError(null);
    }
  }, [visible, form, projectname]);

  function buildRtspUrl(vals) {
    const safePort = vals.port || 554;
    const auth = `${vals.cameraUsername || "admin"}:${vals.password || ""}`;
    const base = `rtsp://${auth}@${vals.ip}:${safePort}`;
    if (brand === "Hikvision") {
      const ch = connectionType === "nvr" ? Number(vals.channel) : Number(vals.channel || 1);
      return `${base}/Streaming/channels/${ch}01/main`;
    }
    const ch = connectionType === "nvr" ? Number(vals.channel || 1) : 0;
    return `${base}/cam/realmonitor?channel=${ch}&subtype=0`;
  }

  const handleAdd = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const vals = form.getFieldsValue();
    setSaving(true);
    setValidationStatus("validating");
    setValidationError(null);

    try {
      const rtspUrl = buildRtspUrl(vals);

      const valRes = await axios.post("/validate-rtsp", {
        rtspUrl,
        cameraName: vals.name?.trim(),
        projectName: vals.projectName?.trim(),
      });

      if (!valRes.data.success || !valRes.data.valid) {
        const errMsg = valRes.data.message || "Stream is not accessible";
        setValidationStatus("invalid");
        setValidationError(errMsg);
        message.error(errMsg);
        setSaving(false);
        return;
      }

      setValidationStatus("valid");

      const payload = {
        user: username,
        organizationId,
        cafeId,
        password: vals.password,
        cameraName: vals.name?.trim(),
        ipAddress: vals.ip?.trim(),
        channel: Number(vals.channel || 1),
        port: Number(vals.port || 554),
        projectName: vals.projectName?.trim(),
        location: vals.location?.trim(),
        username: vals.cameraUsername,
        connectionType,
        brand,
      };

      const saveRes = await axios.post("/add-camera", payload);
      if (saveRes.data.success) {
        message.success("Camera added successfully");
        onAdded(saveRes.data.camera || payload);
        onClose();
      } else {
        message.error(saveRes.data.message || "Failed to add camera");
        setValidationStatus("invalid");
        setValidationError(saveRes.data.message);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || "Network error";
      setValidationStatus("invalid");
      setValidationError(errMsg);
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const nameRules = [
    { required: true, message: "Required" },
    { min: 3, message: "At least 3 characters" },
    { max: 50, message: "Max 50 characters" },
  ];
  const credRules = [{ required: true, message: "Required" }, { min: 2, message: "At least 2 characters" }];
  const passRules = [{ required: true, message: "Required" }, { min: 4, message: "At least 4 characters" }];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "linear-gradient(135deg, #27AE60, #52B788)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlusOutlined style={{ color: "#fff", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1A2742" }}>Add New Camera</div>
            <div style={{ fontSize: 12, color: "#7B8FB5", fontWeight: 400 }}>
              Configure and validate before saving
              {brand && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: "1px 8px",
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    background: brand === "Hikvision" ? "#fee2e2" : "#dbeafe",
                    color: brand === "Hikvision" ? "#b91c1c" : "#1d4ed8",
                  }}
                >
                  {brand}
                </span>
              )}
            </div>
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ValidationBadge status={validationStatus} error={validationError} />
          <Space>
            <Button onClick={onClose} icon={<CloseOutlined />}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleAdd}
              style={{ background: "#27AE60", border: "none" }}
            >
              Validate & Add
            </Button>
          </Space>
        </div>
      }
      width={560}
      styles={{ content: { borderRadius: 18 }, header: { borderRadius: "18px 18px 0 0" } }}
    >
      {/* Connection Type Toggle */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {[
          { value: "direct", label: "Direct Camera", desc: "Camera has its own IP" },
          { value: "nvr", label: "NVR / DVR", desc: "Camera is on a recorder" },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              setConnectionType(opt.value);
              setValidationStatus(null);
              setValidationError(null);
            }}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 10,
              border: connectionType === opt.value ? "2px solid #4A90D9" : "2px solid #E8EDF5",
              background: connectionType === opt.value ? "#EBF3FF" : "#fff",
              color: connectionType === opt.value ? "#1d4ed8" : "#4A5568",
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.15s",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
            <div style={{ fontSize: 11, color: "#7B8FB5", marginTop: 2 }}>{opt.desc}</div>
          </button>
        ))}
      </div>

      <Form form={form} layout="vertical" size="middle">
        {/* Name + Location */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item label="Camera Name" name="name" rules={nameRules}>
            <Input placeholder="e.g. Main Entrance" />
          </Form.Item>
          <Form.Item label="Location" name="location" rules={nameRules}>
            <Input placeholder="e.g. Gate 1, Floor 2" />
          </Form.Item>
        </div>

        {/* Project (disabled) */}
        <Form.Item label="Project Name" name="projectName">
          <Input disabled />
        </Form.Item>

        {/* Network */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: connectionType === "nvr" ? "1fr 1fr 1fr" : "1fr 1fr",
            gap: 12,
          }}
        >
          <Form.Item
            label={connectionType === "nvr" ? "NVR IP Address" : "Camera IP Address"}
            name="ip"
            rules={ipRules}
          >
            <Input placeholder="192.168.1.100" />
          </Form.Item>
          <Form.Item label="Port" name="port" rules={portRules}>
            <Input type="number" placeholder="554" />
          </Form.Item>
          {connectionType === "nvr" && (
            <Form.Item label="Channel" name="channel" rules={channelRules}>
              <Input type="number" placeholder="1" />
            </Form.Item>
          )}
        </div>

        {/* Credentials */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item
            label={connectionType === "nvr" ? "NVR Username" : "Camera Username"}
            name="cameraUsername"
            rules={credRules}
          >
            <Input placeholder="admin" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label={connectionType === "nvr" ? "NVR Password" : "Camera Password"}
            name="password"
            rules={passRules}
          >
            <Input.Password placeholder="Password" autoComplete="new-password" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

// ==========================================================================
// CAMERA CARD (grid view)
// ==========================================================================

const CameraCard = ({ cam, onView, onEdit }) => (
  <Card
    hoverable
    className="h-full transition-all duration-200"
    style={{
      borderRadius: 14,
      border: "1px solid #E8EDF5",
      boxShadow: "0 2px 12px rgba(74,144,217,0.06)",
      background: "#FAFCFF",
    }}
    bodyStyle={{ padding: "20px 22px" }}
  >
    {/* Header */}
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <Avatar
          size={44}
          style={{
            background: avatarColor(cam.cameraName),
            fontWeight: 600,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {initials(cam.cameraName)}
        </Avatar>
        <div>
          <Text strong style={{ fontSize: 15, color: "#1A2742", display: "block" }}>
            {cam.cameraName}
          </Text>
          <Text style={{ fontSize: 12, color: "#7B8FB5" }}>{cam.code}</Text>
        </div>
      </div>
      <Tag
        style={{
          borderRadius: 20,
          fontWeight: 600,
          fontSize: 11,
          padding: "2px 10px",
          border: "none",
          background: cam.active ? "#E6F7EF" : "#FFF0F0",
          color: cam.active ? "#27AE60" : "#E74C3C",
        }}
      >
        {cam.active ? "● Live" : "● Offline"}
      </Tag>
    </div>

    {/* Stats row */}
    <div
      className="grid grid-cols-3 gap-2 mb-4"
      style={{
        background: "#F0F5FF",
        borderRadius: 10,
        padding: "10px 12px",
      }}
    >
      {[
        { label: "IP", value: cam.ipAddress },
        { label: "Port", value: cam.port },
        { label: "Ch", value: cam.channel },
      ].map(({ label, value }) => (
        <div key={label} className="text-center">
          <div style={{ fontSize: 11, color: "#7B8FB5", marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1A2742" }}>{value}</div>
        </div>
      ))}
    </div>

    {/* Details */}
    <div className="space-y-2 mb-4">
      {[
        { icon: <EnvironmentOutlined />, label: cam.location },
        { icon: <AppstoreOutlined />, label: cam.projectName },
        { icon: <UserOutlined />, label: `Added by ${cam.addedBy}` },
        { icon: <CalendarOutlined />, label: formatDate(cam.createdAt) },
      ].map(({ icon, label }, i) => (
        <div key={i} className="flex items-center gap-2">
          <span style={{ color: "#4A90D9", fontSize: 13 }}>{icon}</span>
          <Text style={{ fontSize: 13, color: "#4A5568" }}>{label}</Text>
        </div>
      ))}
    </div>

    <Divider style={{ margin: "12px 0", borderColor: "#E8EDF5" }} />

    <div className="flex items-center justify-between">
      <Text style={{ fontSize: 11, color: "#7B8FB5" }}>
        Model: <span style={{ color: "#4A5568", fontWeight: 500 }}>{cam.modelName}</span>
      </Text>
      <Space size={6}>
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(cam)}
          style={{ borderRadius: 8, fontSize: 12, borderColor: "#4A90D9", color: "#4A90D9" }}
        >
          Edit
        </Button>
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => onView(cam)}
          style={{ borderRadius: 8, background: "#4A90D9", border: "none", fontSize: 12 }}
        >
          View
        </Button>
      </Space>
    </div>
  </Card>
);

// ==========================================================================
// CAMERA DETAIL DRAWER (inline modal)
// ==========================================================================

const CameraDetail = ({ cam, onClose, onEdit }) => {
  if (!cam) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(10,20,50,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 18,
          width: "100%",
          maxWidth: 560,
          padding: "28px 32px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.14)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Avatar
              size={52}
              style={{ background: avatarColor(cam.cameraName), fontWeight: 700, fontSize: 18 }}
            >
              {initials(cam.cameraName)}
            </Avatar>
            <div>
              <Title level={4} style={{ margin: 0, color: "#1A2742" }}>
                {cam.cameraName}
              </Title>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cam.active ? (
              <Tag color="success" style={{ borderRadius: 20 }}>
                <CheckCircleFilled /> Live
              </Tag>
            ) : (
              <Tag color="error" style={{ borderRadius: 20 }}>
                <CloseCircleFilled /> Offline
              </Tag>
            )}
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { onClose(); onEdit(cam); }}
              style={{ borderRadius: 8, borderColor: "#4A90D9", color: "#4A90D9" }}
            >
              Edit
            </Button>
            <Button size="small" onClick={onClose} style={{ borderRadius: 8 }}>
              ✕
            </Button>
          </div>
        </div>

        {/* Stream info */}
        <Alert
          message={
            <span style={{ fontFamily: "monospace", fontSize: 13 }}>
              rtsp://{cam.username}:****@{cam.ipAddress}:{cam.port}/channel/{cam.channel}
            </span>
          }
          type="info"
          icon={<WifiOutlined />}
          showIcon
          style={{ borderRadius: 10, marginBottom: 20 }}
        />

        <Descriptions
          column={2}
          size="small"
          bordered
          labelStyle={{ background: "#F7F9FF", color: "#7B8FB5", fontWeight: 500, fontSize: 13 }}
          contentStyle={{ fontSize: 13, color: "#1A2742" }}
          style={{ borderRadius: 10, overflow: "hidden", border: "1px solid #E8EDF5" }}
        >
          <Descriptions.Item label="IP Address" span={1}>{cam.ipAddress}</Descriptions.Item>
          <Descriptions.Item label="Port">{cam.port}</Descriptions.Item>
          <Descriptions.Item label="Channel">{cam.channel}</Descriptions.Item>
          <Descriptions.Item label="Model">{cam.modelName}</Descriptions.Item>
          <Descriptions.Item label="Location">{cam.location}</Descriptions.Item>
          <Descriptions.Item label="Project">{cam.projectName}</Descriptions.Item>
          <Descriptions.Item label="Cafe ID">{cam.cafeId}</Descriptions.Item>
          <Descriptions.Item label="Org ID">{cam.organizationId}</Descriptions.Item>
          <Descriptions.Item label="Username">{cam.username}</Descriptions.Item>
          <Descriptions.Item label="Added By">{cam.addedBy}</Descriptions.Item>
          <Descriptions.Item label="Modified By">{cam.modifiedBy || "—"}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge status={cam.active ? "success" : "error"} text={cam.active ? "Active" : "Inactive"} />
          </Descriptions.Item>
          <Descriptions.Item label="Created At" span={2}>{formatDate(cam.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Updated At" span={2}>{formatDate(cam.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </div>
    </div>
  );
};

// ==========================================================================
// MAIN DASHBOARD
// ==========================================================================

export default function CameraDashboard() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [selected, setSelected] = useState(null);

  // Modal state
  const [editCam, setEditCam] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
  const organizationId = localStorage.getItem("organizationId");

  const fetchCameras = async () => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-cameras", { organizationId });
      if (res.data.success) {
        setCameras(res.data.cameras || []);
      } else {
        message.error(res.data.message || "Failed to fetch cameras");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Error fetching cameras");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  // Update a camera in local state after edit
  const handleCameraUpdated = useCallback((updatedCam) => {
    setCameras((prev) =>
      prev.map((c) => (c.code === updatedCam.code ? { ...c, ...updatedCam } : c))
    );
  }, []);

  // Add a camera to local state after add
  const handleCameraAdded = useCallback((newCam) => {
    setCameras((prev) => [newCam, ...prev]);
  }, []);

  const filtered = cameras.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      c.cameraName?.toLowerCase().includes(q) ||
      c.ipAddress?.includes(q) ||
      c.location?.toLowerCase().includes(q) ||
      c.code?.toLowerCase().includes(q);
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && c.active) ||
      (statusFilter === "inactive" && !c.active);
    return matchSearch && matchStatus;
  });

  const total = cameras.length;
  const active = cameras.filter((c) => c.active).length;
  const inactive = total - active;

  return (
    <>
      <Header auth={citriot?.auth} />

      <div style={{ minHeight: "100vh", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <Breadcrumb
            className="mb-4 ml-1"
            items={[
              {
                title: (
                  <button
                    onClick={() => navigate("/selectmodel")}
                    className="text-blue-600 hover:text-blue-700 font-medium bg-none border-none cursor-pointer p-0"
                  >
                    Select Modal
                  </button>
                ),
              },
              {
                title: <span className="text-gray-600 font-medium">View Camera</span>,
              },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {/* Stats */}
          <Row gutter={[16, 16]} className="mb-6">
            {[
              { label: "Total Cameras", value: total, color: "#4A90D9", bg: "#EBF3FF" },
              { label: "Live", value: active, color: "#27AE60", bg: "#E6F7EF" },
              { label: "Offline", value: inactive, color: "#E74C3C", bg: "#FFF0F0" },
            ].map(({ label, value, color, bg }) => (
              <Col xs={8} key={label}>
                <Card
                  style={{
                    borderRadius: 14,
                    border: "1px solid #E8EDF5",
                    background: bg,
                    textAlign: "center",
                  }}
                  bodyStyle={{ padding: "16px 12px" }}
                >
                  <Statistic
                    title={<Text style={{ fontSize: 12, color: "#7B8FB5" }}>{label}</Text>}
                    value={value}
                    valueStyle={{ color, fontWeight: 700, fontSize: 28 }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          {/* Filters + Add Button */}
          <div
            className="flex flex-wrap gap-3 items-center mb-6"
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: "14px 18px",
              border: "1px solid #E8EDF5",
            }}
          >
            <Search
              placeholder="Search by name, IP, location…"
              prefix={<SearchOutlined style={{ color: "#A0B0CC" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "1 1 220px", minWidth: 180 }}
              allowClear
            />
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }}>
              <Option value="all">All Status</Option>
              <Option value="active">Live Only</Option>
              <Option value="inactive">Offline Only</Option>
            </Select>
            <Space>
              <Tooltip title="Grid View">
                <Button
                  type={viewMode === "grid" ? "primary" : "default"}
                  icon={<AppstoreOutlined />}
                  onClick={() => setViewMode("grid")}
                  style={{
                    borderRadius: 8,
                    background: viewMode === "grid" ? "#4A90D9" : undefined,
                    border: viewMode === "grid" ? "none" : undefined,
                  }}
                />
              </Tooltip>
              <Tooltip title="List View">
                <Button
                  type={viewMode === "list" ? "primary" : "default"}
                  icon={<UnorderedListOutlined />}
                  onClick={() => setViewMode("list")}
                  style={{
                    borderRadius: 8,
                    background: viewMode === "list" ? "#4A90D9" : undefined,
                    border: viewMode === "list" ? "none" : undefined,
                  }}
                />
              </Tooltip>
              <Tooltip title="Refresh">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchCameras}
                  style={{ borderRadius: 8 }}
                />
              </Tooltip>
            </Space>

            {/* Add Camera Button */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddModal(true)}
              style={{
                borderRadius: 8,
                background: "#27AE60",
                border: "none",
                fontWeight: 600,
                marginLeft: "auto",
              }}
            >
              Add Camera
            </Button>

            <Text style={{ fontSize: 13, color: "#7B8FB5" }}>
              {filtered.length} of {total} cameras
            </Text>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center items-center py-24">
              <Spin size="large" />
            </div>
          ) : filtered.length === 0 ? (
            <Empty
              description={<Text style={{ color: "#7B8FB5" }}>No cameras found</Text>}
              style={{ padding: "60px 0" }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowAddModal(true)}
                style={{ background: "#27AE60", border: "none" }}
              >
                Add Your First Camera
              </Button>
            </Empty>
          ) : viewMode === "grid" ? (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
            >
              {filtered.map((cam) => (
                <CameraCard
                  key={cam.code}
                  cam={cam}
                  onView={setSelected}
                  onEdit={setEditCam}
                />
              ))}
            </div>
          ) : (
            /* List View */
            <div className="flex flex-col gap-3">
              {filtered.map((cam) => (
                <Card
                  key={cam.code}
                  hoverable
                  style={{
                    borderRadius: 12,
                    border: "1px solid #E8EDF5",
                    background: "#FAFCFF",
                    boxShadow: "0 2px 8px rgba(74,144,217,0.05)",
                  }}
                  bodyStyle={{ padding: "14px 20px" }}
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <Avatar
                      size={40}
                      style={{
                        background: avatarColor(cam.cameraName),
                        fontWeight: 600,
                        fontSize: 15,
                        flexShrink: 0,
                      }}
                    >
                      {initials(cam.cameraName)}
                    </Avatar>
                    <div style={{ flex: "1 1 160px" }}>
                      <Text strong style={{ color: "#1A2742", display: "block" }}>
                        {cam.cameraName}
                      </Text>
                      <Text style={{ fontSize: 12, color: "#7B8FB5" }}>{cam.code}</Text>
                    </div>
                    <div
                      className="flex flex-wrap gap-x-6 gap-y-1 items-center"
                      style={{ flex: "2 1 300px" }}
                    >
                      {[
                        { label: "IP", value: cam.ipAddress },
                        { label: "Port", value: cam.port },
                        { label: "Ch", value: cam.channel },
                        { label: "Location", value: cam.location },
                      ].map(({ label, value }) => (
                        <div key={label}>
                          <Text style={{ fontSize: 11, color: "#7B8FB5" }}>{label} </Text>
                          <Text style={{ fontSize: 13, fontWeight: 600, color: "#1A2742" }}>
                            {value}
                          </Text>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 ml-auto">
                      <Tag
                        style={{
                          borderRadius: 20,
                          fontWeight: 600,
                          fontSize: 11,
                          border: "none",
                          background: cam.active ? "#E6F7EF" : "#FFF0F0",
                          color: cam.active ? "#27AE60" : "#E74C3C",
                        }}
                      >
                        {cam.active ? "● Live" : "● Offline"}
                      </Tag>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setEditCam(cam)}
                        style={{ borderRadius: 8, borderColor: "#4A90D9", color: "#4A90D9" }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => setSelected(cam)}
                        style={{ borderRadius: 8, background: "#4A90D9", border: "none" }}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <CameraDetail
          cam={selected}
          onClose={() => setSelected(null)}
          onEdit={(cam) => { setSelected(null); setEditCam(cam); }}
        />

        {/* Edit Camera Modal */}
        <EditCameraModal
          cam={editCam}
          visible={!!editCam}
          onClose={() => setEditCam(null)}
          onSaved={handleCameraUpdated}
        />

        {/* Add Camera Modal */}
        <AddCameraModal
          visible={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdded={handleCameraAdded}
        />
      </div>
    </>
  );
}